// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

const DATA_URL = 'https://ix.cnn.io/data/consumer-sentiment/consumer_sentiment_summary.json';
const PURPLE = '#7c4ea5';
const WINDOW_YEARS = 20;

const parseDate = d3.timeParse('%Y-%m-%d');
const fmtMonthYear = d3.timeFormat('%b %Y');
const fmtMonthLong = d3.timeFormat('%B %Y');
const fmtMonthName = d3.timeFormat('%B');
const fmtYearShort = d3.timeFormat('%y');

// Recessions falling inside the visible window. Bands are clamped to the plot,
// so an entry that scrolls out of the window simply stops drawing.
const recessions = [
  {
    start: '2007-12-01',
    end: '2009-06-01',
    label: ['Great', 'Recession'],
    labelMobile: ['Great', 'Recession'],
  },
  {
    start: '2020-02-01',
    end: '2020-04-01',
    label: ['Covid-19', 'recession'],
    labelMobile: ['Covid'],
  },
];

let series = []; // continuous monthly sequence for the visible window; value may be null
let points = []; // reported values only
let lastPoint;
let windowLow;
let isSeriesRecordLow = false;
let windowMean;
let minDomain;
let maxDomain;
let yMax;
let resizeTimer;

// eslint-disable-next-line no-unused-vars
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    // eslint-disable-next-line no-unused-vars
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;

stylePremium(CNNPolitics, CNNBusiness);

// Round through two decimals before formatting to one, so values sitting on a
// rounding boundary (3.15) don't fall to 3.1 through binary representation.
function fmtValue(v) {
  return (Math.round(Math.round(v * 100) / 10) / 10).toFixed(1);
}

function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;

  const margins = {
    top: 16,
    right: isMobile ? 62 : 84,
    bottom: 24,
    left: isMobile ? 30 : 34,
  };

  const height = Math.round(Math.min(460, Math.max(300, initialWidth * 0.5625)));
  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;
  const xTicks = isMobile ? 3 : 5;
  const yTicks = isMobile ? 4 : 5;

  container.append('p')
    .attr('class', 'chart-title')
    .text('Monthly index value, 1966 = 100');

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([0, yMax])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom(xScale)
    .ticks(xTicks)
    .tickSize(0)
    .tickPadding(8)
    .tickFormat((d) => {
      if (d.getMonth() === 0) return d3.timeFormat('%Y')(d);
      return fmtMonthYear(d);
    });

  const yAxis = d3.axisLeft(yScale)
    .ticks(yTicks)
    .tickSize(-chartWidth)
    .tickPadding(8)
    .tickFormat(d => d);

  const defined = d => d.value !== null;

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .defined(defined)
    .x(d => xScale(d.date))
    .y(d => yScale(d.value));

  const area = d3.area()
    .curve(d3.curveMonotoneX)
    .defined(defined)
    .x(d => xScale(d.date))
    .y0(yScale(0))
    .y1(d => yScale(d.value));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height)
    .attr('role', 'img')
    .attr('aria-label', `Line chart of the University of Michigan Index of Consumer Sentiment by month, ${fmtMonthLong(minDomain)} through ${fmtMonthLong(maxDomain)}. The latest reading is ${fmtValue(lastPoint.value)}, against a ${WINDOW_YEARS}-year average of ${Math.round(windowMean)}.`);

  const g = svg.append('g')
    .attr('transform', `translate(${margins.left}, ${margins.top})`);

  // Recession bands sit beneath the gridlines so a 1px rule stays visible
  // inside them, and light enough that the series fill reads the same in or out.
  const bands = g.append('g').attr('class', 'recessions');
  const bandLabels = [];

  recessions.forEach((rec) => {
    const x0 = Math.max(0, xScale(parseDate(rec.start)));
    const x1 = Math.min(chartWidth, xScale(parseDate(rec.end)));

    if (x1 <= 0 || x0 >= chartWidth) return;

    // A two-month recession is barely two pixels wide across a 20-year window,
    // so hold a floor that keeps the band perceptible.
    const width = Math.max(6, x1 - x0);

    bands.append('rect')
      .attr('class', 'recession-band')
      .attr('x', x0)
      .attr('width', width)
      .attr('y', 0)
      .attr('height', chartHeight);

    bandLabels.push({
      x: Math.max(x0 + width, x1) + 5,
      lines: isMobile ? rec.labelMobile : rec.label,
    });
  });

  g.append('path')
    .attr('class', 'area')
    .datum(series)
    .attr('fill', PURPLE)
    .attr('d', area);

  // Gridlines ride above the fill. Buried underneath it, a reader can't trace a
  // value in the filled region back to the axis.
  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(0, ${chartHeight})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  g.selectAll('.grid .domain').remove();

  g.append('line')
    .attr('class', 'zero-line')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', yScale(0))
    .attr('y2', yScale(0));

  // 20-year average, labeled at the end of the rule rather than in a legend
  g.append('line')
    .attr('class', 'avg-line')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', yScale(windowMean))
    .attr('y2', yScale(windowMean));

  const avgLabel = g.append('g')
    .attr('class', 'avg-label')
    .attr('transform', `translate(${chartWidth + 6}, ${yScale(windowMean)})`);

  avgLabel.append('text')
    .attr('class', 'avg-text')
    .attr('dy', '-0.2em')
    .text(isMobile ? `${WINDOW_YEARS}-yr avg.` : `${WINDOW_YEARS}-year avg.`);

  avgLabel.append('text')
    .attr('class', 'avg-text-value')
    .attr('dy', '1em')
    .text(Math.round(windowMean));

  const lineWidth = isMobile ? 1.5 : 2;

  g.append('path')
    .attr('class', 'series-line')
    .datum(series)
    .attr('stroke', PURPLE)
    .attr('stroke-width', lineWidth)
    .attr('d', path);

  // Recession labels ride the top of the plot, where the fill never reaches
  bandLabels.forEach((band) => {
    band.lines.forEach((linetext, i) => {
      g.append('text')
        .attr('class', 'label-recession')
        .attr('x', band.x)
        .attr('y', 11 + (i * 13))
        .attr('text-anchor', 'start')
        .text(linetext);
    });
  });

  const lowIsLatest = windowLow.date.getTime() === lastPoint.date.getTime();
  const lowWording = isSeriesRecordLow ? 'Record low' : `Lowest in ${WINDOW_YEARS} years`;

  // Dynamic low annotation. Skipped when the low is also the latest reading,
  // since the endpoint label already sits there.
  if (!lowIsLatest) {
    const lowX = xScale(windowLow.date);
    const frac = lowX / chartWidth;
    let anchor = 'middle';
    let labelDx = 0;

    if (frac > 0.7) {
      anchor = 'end';
      labelDx = -5;
    } else if (frac < 0.2) {
      anchor = 'start';
      labelDx = 5;
    }

    const low = g.append('g')
      .attr('class', 'low-annotation')
      .attr('transform', `translate(${lowX}, ${yScale(windowLow.value)})`);

    low.append('line')
      .attr('class', 'leader')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 7)
      .attr('y2', 24);

    low.append('circle')
      .attr('class', 'point-marker')
      .attr('r', 3.5);

    low.append('text')
      .attr('class', 'low-label')
      .attr('text-anchor', anchor)
      .attr('x', labelDx)
      .attr('y', 36)
      .text(lowWording);

    const lowDate = isMobile
      ? `${d3.timeFormat('%b')(windowLow.date)} '${fmtYearShort(windowLow.date)}`
      : fmtMonthYear(windowLow.date);

    low.append('text')
      .attr('class', 'low-value')
      .attr('text-anchor', anchor)
      .attr('x', labelDx)
      .attr('y', 50)
      .text(`${lowDate}: ${fmtValue(windowLow.value)}`);
  }

  // Latest reading, labeled at the end of the line
  const annotation = g.append('g')
    .attr('class', 'annotation')
    .attr('transform', `translate(${xScale(lastPoint.date)}, ${yScale(lastPoint.value)})`);

  // A filled disc at the marker's own radius, drawn behind it, so the pulse
  // reads as a halo growing out from under the marker.
  const markerR = 4;

  annotation.append('circle')
    .attr('class', 'pulse')
    .attr('r', markerR)
    .attr('fill', PURPLE);

  annotation.append('circle')
    .attr('class', 'point-marker')
    .attr('r', markerR);

  const dateLabel = isMobile
    ? `${d3.timeFormat('%b')(lastPoint.date)} '${fmtYearShort(lastPoint.date)}`
    : fmtMonthYear(lastPoint.date);

  annotation.append('text')
    .attr('class', 'annotation-date')
    .attr('x', 10)
    .attr('dy', '-0.15em')
    .text(lastPoint.isPreliminary ? `${dateLabel}*` : dateLabel);

  annotation.append('text')
    .attr('class', 'annotation-value')
    .attr('x', 10)
    .attr('dy', '1.05em')
    .text(fmtValue(lastPoint.value));

  if (lowIsLatest) {
    annotation.append('text')
      .attr('class', 'low-label')
      .attr('x', 10)
      .attr('dy', '2.25em')
      .text(lowWording);
  }

  // Hover / touch readout
  const bisect = d3.bisector(d => d.date).left;

  const hover = g.append('g')
    .attr('class', 'hover-layer')
    .style('display', 'none');

  hover.append('line')
    .attr('class', 'hover-line')
    .attr('y1', 0)
    .attr('y2', chartHeight);

  hover.append('circle')
    .attr('class', 'hover-dot')
    .attr('r', 4.5);

  const tooltip = container.append('div')
    .attr('class', 'tooltip')
    .style('display', 'none');

  function findPoint(mx) {
    const x0 = xScale.invert(mx);
    const i = bisect(points, x0);
    const a = points[i - 1];
    const b = points[i];

    if (!a) return b;
    if (!b) return a;
    return (x0 - a.date) > (b.date - x0) ? b : a;
  }

  function showReadout(event) {
    const bounds = svg.node().getBoundingClientRect();
    const mx = event.clientX - bounds.left - margins.left;
    const d = findPoint(mx);

    if (!d) return;

    const px = xScale(d.date);
    const py = yScale(d.value);

    hover.style('display', null);
    hover.select('.hover-line').attr('x1', px).attr('x2', px);
    hover.select('.hover-dot').attr('cx', px).attr('cy', py);
    svg.classed('is-hovering', true);

    tooltip
      .style('display', null)
      .html(`<span class="tt-date">${fmtMonthLong(d.date)}</span><span class="tt-value">${fmtValue(d.value)}</span>${d.isPreliminary ? '<span class="tt-flag">Preliminary</span>' : ''}`);

    const ttWidth = tooltip.node().offsetWidth;
    const ttHeight = tooltip.node().offsetHeight;
    const left = Math.max(
      4,
      Math.min(initialWidth - ttWidth - 4, margins.left + px - (ttWidth / 2)),
    );

    // Flip below the point when there isn't room above it
    const above = (margins.top + py) > (ttHeight + 8);

    tooltip
      .classed('is-below', !above)
      .style('left', `${left}px`)
      .style('top', `${margins.top + py + (above ? -12 : 12)}px`);
  }

  function hideReadout() {
    hover.style('display', 'none');
    tooltip.style('display', 'none');
    svg.classed('is-hovering', false);
  }

  // Native listeners rather than selection.on, so the handler signature doesn't
  // depend on which d3 major version the rig resolves.
  const capture = g.append('rect')
    .attr('class', 'hover-capture')
    .attr('width', chartWidth)
    .attr('height', chartHeight)
    .node();

  capture.addEventListener('pointerdown', showReadout);
  capture.addEventListener('pointermove', showReadout);
  capture.addEventListener('pointerleave', hideReadout);
  capture.addEventListener('pointercancel', hideReadout);

  window.pymChild.sendHeight();
}

d3.json(DATA_URL).then((jsondata) => {
  const rawData = jsondata.data.map(d => ({
    date: parseDate(d.date),
    value: +d.value,
    isPreliminary: !!d.is_preliminary,
  }));

  const maxDate = d3.max(rawData, d => d.date);
  const windowStart = new Date(maxDate);
  windowStart.setFullYear(windowStart.getFullYear() - WINDOW_YEARS);

  // Build the window from a continuous month sequence rather than from whatever
  // rows the feed happens to carry, so a missing month arrives as a null and
  // breaks the line instead of disappearing into an interpolated segment.
  const byMonth = new Map(rawData.map(d => [d3.timeFormat('%Y-%m')(d.date), d]));

  series = d3.timeMonths(
    d3.timeMonth.floor(windowStart),
    d3.timeMonth.offset(d3.timeMonth.floor(maxDate), 1),
  ).map((date) => {
    const match = byMonth.get(d3.timeFormat('%Y-%m')(date));
    return {
      date,
      value: match ? match.value : null,
      isPreliminary: match ? match.isPreliminary : false,
    };
  });

  points = series.filter(d => d.value !== null);
  lastPoint = points[points.length - 1];
  minDomain = series[0].date;
  maxDomain = series[series.length - 1].date;
  yMax = Math.ceil((d3.max(points, d => d.value) + 8) / 10) * 10;

  windowLow = points.reduce((a, b) => (b.value < a.value ? b : a));
  const seriesLow = rawData.reduce((a, b) => (b.value < a.value ? b : a));
  isSeriesRecordLow = seriesLow.date.getTime() === windowLow.date.getTime();

  // Computed from the drawn window rather than read from metadata, so the rule
  // and its label always describe what's on screen. Matches the feed's
  // last_20_years_mean, which covers the same months.
  windowMean = d3.mean(points, d => d.value);

  const dek = jsondata.metadata.summary_sentence;
  const lastUpdated = jsondata.metadata.last_updated_str;

  d3.select('h2.dek').html(dek);

  const note = [`Data current through ${fmtMonthLong(lastPoint.date)} as of ${lastUpdated}.`];

  if (lastPoint.isPreliminary) {
    note.push(`*${fmtMonthName(lastPoint.date)} is preliminary, based on the first half of the month's survey responses, and will be revised.`);
  }

  d3.select('p.timestamp').text(note.join(' '));

  draw();

  window.onresize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(draw, 150);
  };
});
